import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Link, useSearchParams } from "react-router-dom";

import axios from "axios";

import Icons from "../components/common/Icons";
import { ArrowFillButton } from "../components/Dashboard/MissionPage/ArrowFillButton";
import { useAuthContext } from "../context/auth-context";
import useReducedMotion from "../hooks/useReducedMotion";
import axiosInstance from "../utils/axiosInstance";
import { SuccessToast } from "../utils/customToast";
import { validateFormField } from "../utils/validateFormField";
import { isWebGLAvailable } from "../utils/webglSupport";

// Lazy-loaded three.js background so the WebGL stack stays out of the
// main bundle. The CSS starfield below is the instant-paint fallback.
const CosmicScene = lazy(() => import("../components/Login/CosmicScene"));

// ============================================
// COSMIC SCENE CHUNK BOUNDARY
// ============================================
// Non-lazy error boundary ABOVE the Suspense. The SceneErrorBoundary
// inside CosmicScene.jsx lives in the lazy module and cannot catch the
// rejection of its own chunk load (deploy skew with rotated chunk
// hashes, flaky network, content filters blocking the secondary JS
// request). Without this, React 18 unmounts the whole root and the
// login page white-screens. onError flips the Login state back to the
// CSS starfield.
class CosmicSceneChunkBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    if (this.props.onError) this.props.onError();
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

// ============================================
// COSMIC STAR FIELD COMPONENT
// ============================================
const CosmicStarField = ({ isLaunching }) => {
  const stars = useMemo(() => {
    return Array.from({ length: 80 }, (_, i) => {
      const x = Math.random() * 100;
      const y = Math.random() * 100;

      // Vary star sizes
      const sizeRandom = Math.random();
      let size;
      if (sizeRandom > 0.92) {
        size = 3 + Math.random() * 1.5; // Large stars
      } else if (sizeRandom > 0.7) {
        size = 2 + Math.random() * 1; // Medium stars
      } else {
        size = 1 + Math.random() * 1; // Small stars
      }

      // Twinkle properties
      // F02 calm sky: roughly one star in seven breathes, slowly. The same
      // random draws are taken, so the field itself is unchanged.
      const doesTwinkle = Math.random() > 0.86;
      const twinkleDuration = 6 + Math.random() * 4;
      const twinkleDelay = Math.random() * 5;
      const baseOpacity = 0.3 + Math.random() * 0.5;

      // Star colors - cosmic palette
      const colorRandom = Math.random();
      let color;
      if (colorRandom > 0.9) {
        color = "rgba(171, 235, 247, 1)"; // Charlotte blue
      } else if (colorRandom > 0.82) {
        color = "rgba(218, 236, 55, 0.9)"; // Pear accent
      } else if (colorRandom > 0.65) {
        color = "rgba(210, 218, 239, 1)"; // Cool white
      } else {
        color = "rgba(255, 255, 255, 1)"; // Pure white
      }

      return {
        id: i,
        x,
        y,
        size,
        doesTwinkle,
        twinkleDuration,
        twinkleDelay,
        baseOpacity,
        color,
      };
    });
  }, []);

  const getStarAnimation = (star) => {
    if (isLaunching) {
      return `warpSpeed 800ms ease-in forwards ${Math.random() * 200}ms`;
    }
    if (star.doesTwinkle) {
      return `loginStarTwinkle ${star.twinkleDuration}s ease-in-out ${star.twinkleDelay}s infinite`;
    }
    return "none";
  };

  const getStarShadow = (star) => {
    if (star.size > 2) {
      return `0 0 ${star.size * 4}px ${star.color.replace("1)", "0.6)")}`;
    }
    return `0 0 ${star.size * 2}px ${star.color.replace("1)", "0.4)")}`;
  };

  return (
    <div className="xe-f02-sky absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            backgroundColor: star.color,
            opacity: star.baseOpacity,
            boxShadow: getStarShadow(star),
            animation: getStarAnimation(star),
            "--star-opacity": star.baseOpacity,
            transition: "all 0.3s ease",
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// NEBULA EFFECTS COMPONENT
// ============================================
const NebulaEffects = () => (
  <div className="xe-f02-sky absolute inset-0 overflow-hidden pointer-events-none">
    {/* Main nebula blob - top right */}
    <div
      className="absolute rounded-full"
      style={{
        width: "600px",
        height: "600px",
        top: "-20%",
        right: "-10%",
        background:
          "radial-gradient(ellipse at center, rgba(171, 235, 247, 0.08) 0%, rgba(25, 42, 77, 0.05) 40%, transparent 70%)",
        filter: "blur(60px)",
        animation: "nebulaDrift 25s ease-in-out infinite",
      }}
    />
    {/* Secondary nebula - bottom left */}
    <div
      className="absolute rounded-full"
      style={{
        width: "500px",
        height: "500px",
        bottom: "-15%",
        left: "-15%",
        background:
          "radial-gradient(ellipse at center, rgba(255, 90, 95, 0.06) 0%, rgba(32, 53, 96, 0.04) 40%, transparent 70%)",
        filter: "blur(50px)",
        animation: "nebulaDrift2 30s ease-in-out infinite",
      }}
    />
    {/* Accent nebula - center */}
    <div
      className="absolute rounded-full"
      style={{
        width: "400px",
        height: "400px",
        top: "30%",
        left: "50%",
        transform: "translateX(-50%)",
        background:
          "radial-gradient(ellipse at center, rgba(218, 236, 55, 0.03) 0%, transparent 60%)",
        filter: "blur(80px)",
        animation: "nebulaDrift 35s ease-in-out infinite reverse",
      }}
    />
    {/* Subtle vignette */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(3, 13, 33, 0.4) 100%)",
      }}
    />
  </div>
);

// ============================================
// STYLE HELPERS
// ============================================
const getInputStyle = (hasInputError) => ({
  background: "rgba(25, 42, 77, 0.5)",
  border: hasInputError
    ? "1px solid rgba(255, 90, 95, 0.7)"
    : "1px solid rgba(171, 235, 247, 0.15)",
  boxShadow: hasInputError ? "0 0 20px rgba(255, 90, 95, 0.2)" : "none",
});

// F02: the card is lit once and holds still. The edge light is the resting
// frame of the old border pulse; a failed sign-in is told by the field and its
// message, never by shaking the card (brief E31, micro-state "failure").
const CARD_EDGE_LIGHT =
  "0 0 24px rgba(171, 235, 247, 0.10), 0 24px 60px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.05)";
const CARD_EDGE_LIGHT_ERROR =
  "0 0 24px rgba(255, 90, 95, 0.16), 0 24px 60px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.05)";

// ============================================
// MAIN LOGIN COMPONENT
// ============================================
export const Login = () => {
  const { signin, handleCleverCallback } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [errors, setErrors] = useState({ error: "" });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isCleverEnabled, setIsCleverEnabled] = useState(false);
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);

  // Animation entrance state
  const [isVisible, setIsVisible] = useState(false);

  // 3D background state. The three.js layer mounts only when the device
  // supports WebGL and the user has not requested reduced motion. The CSS
  // starfield stays rendered until the 3D scene signals ready, then fades.
  const prefersReducedMotion = useReducedMotion();
  const [webglOk] = useState(() => isWebGLAvailable());
  const [sceneFailed, setSceneFailed] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [cssStarsRetired, setCssStarsRetired] = useState(false);

  const enable3D = webglOk && !prefersReducedMotion && !sceneFailed;

  const handleSceneReady = useCallback(() => setSceneReady(true), []);
  const handleSceneError = useCallback(() => {
    // Loud fallback: keep a marker in the console so a dead 3D layer is
    // diagnosable in the field (the CSS starfield takes over visually).
    console.warn("[Login] 3D cosmic scene failed; using CSS starfield");
    setSceneFailed(true);
    setSceneReady(false);
    setCssStarsRetired(false);
  }, []);

  // Unmount the CSS starfield once the 3D layer has faded in (the 800ms
  // opacity transition below finishes before this fires).
  useEffect(() => {
    if (!sceneReady) return undefined;
    const timer = setTimeout(() => setCssStarsRetired(true), 900);
    return () => clearTimeout(timer);
  }, [sceneReady]);

  // Handle Clever callback
  useEffect(() => {
    const token = searchParams.get("token");
    const expiresIn = searchParams.get("expires_in");
    if (token && handleCleverCallback) {
      handleCleverCallback(token, expiresIn);
    }
  }, [searchParams, handleCleverCallback]);

  // Check Clever status
  useEffect(() => {
    const checkCleverStatus = async () => {
      try {
        const response = await axiosInstance("/auth/clever/status", "get");
        setIsCleverEnabled(response.data?.enabled || false);
      } catch (error) {
        setIsCleverEnabled(false);
      }
    };
    checkCleverStatus();
  }, []);

  useEffect(() => {
    // Trigger entrance animation after mount
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Clear error animation after it plays
  useEffect(() => {
    if (hasError) {
      const timer = setTimeout(() => setHasError(false), 600);
      return () => clearTimeout(timer);
    }
  }, [hasError]);

  // Computed styles
  const cardTransform = isVisible
    ? "scale(1) translateY(0)"
    : "scale(0.9) translateY(30px)";
  const elementTransform = isVisible ? "translateY(0)" : "translateY(20px)";
  const visibleOpacity = isVisible ? 1 : 0;

  const handleInputFocus = (e) => {
    if (!errors.error) {
      e.target.style.border = "1px solid rgba(171, 235, 247, 0.5)";
      e.target.style.boxShadow = "0 0 20px rgba(171, 235, 247, 0.15)";
    }
  };

  const handleInputBlur = (e) => {
    if (!errors.error) {
      e.target.style.border = "1px solid rgba(171, 235, 247, 0.15)";
      e.target.style.boxShadow = "none";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;
    const emailValidation = validateFormField(
      event.target.email.value,
      "email",
    );
    /* C1 F28: `existingPassword`, NOT `password`. The creation-time complexity
       rule refused the platform's own roster-generated credential with no
       request and nothing on screen. What this form checks is that something
       was typed; whether it is right is the server's answer and the catch below
       is where it is shown. */
    const passwordValidation = validateFormField(
      event.target.password.value,
      "existingPassword",
    );
    setErrors({
      email: emailValidation,
      password: passwordValidation,
    });

    if (emailValidation === "" && passwordValidation === "") {
      try {
        setIsLoading(true);
        setIsLaunching(true);
        await signin(event.target.email.value, event.target.password.value);
        SuccessToast("Login successful");
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setIsLaunching(false);
        setHasError(true);
        console.error(err);
        const apiMessage = axios.isAxiosError(err)
          ? err.response?.data?.message || err.response?.data?.error
          : err?.message;

        setErrors({
          error:
            axios.isAxiosError(err) && err.code === "ERR_NETWORK"
              ? "Network Error"
              : apiMessage || "you have entered an invalid email or password",
        });
        if (formRef.current) {
          formRef.current.reset();
        }
      }
    }
  };

  const handleCleverLogin = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/clever/redirect`;
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#030D21]">
      {/* Deep space gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #030D21 0%, #0D1526 40%, #132039 70%, #0D1526 100%)",
        }}
      />

      {/* Cosmic elements */}
      {enable3D && (
        <CosmicSceneChunkBoundary onError={handleSceneError}>
          <Suspense fallback={null}>
            <CosmicScene
              isLaunching={isLaunching}
              onReady={handleSceneReady}
              onError={handleSceneError}
            />
          </Suspense>
        </CosmicSceneChunkBoundary>
      )}
      {(!enable3D || !cssStarsRetired) && (
        <div
          data-testid="css-starfield"
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: enable3D && sceneReady ? 0 : 1,
            transition: "opacity 800ms ease",
          }}
        >
          <CosmicStarField isLaunching={isLaunching} />
        </div>
      )}
      <NebulaEffects />

      {/* Success flash overlay */}
      {isLaunching && (
        <div
          className="absolute inset-0 z-50 pointer-events-none"
          style={{
            animation: "successFlash 800ms ease-out forwards",
          }}
        />
      )}

      {/* Main content container */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        {/* Glass-morphic login card */}
        <div
          className="w-full max-w-md"
          style={{
            opacity: visibleOpacity,
            transform: cardTransform,
            transition: "all 700ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <div
            className="relative rounded-3xl p-6 sm:p-8 md:p-10"
            style={{
              background: "rgba(13, 21, 38, 0.78)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: errors.error
                ? "1px solid rgba(255, 90, 95, 0.28)"
                : "1px solid rgba(171, 235, 247, 0.14)",
              boxShadow: hasError ? CARD_EDGE_LIGHT_ERROR : CARD_EDGE_LIGHT,
              transition: "border-color 220ms ease, box-shadow 220ms ease",
            }}
          >
            {/* Logo Section - Hero focal point */}
            <div
              className="flex flex-col items-center mt-4 mb-10"
              style={{
                opacity: visibleOpacity,
                transform: elementTransform,
                transition: "all 600ms cubic-bezier(0.16, 1, 0.3, 1) 200ms",
              }}
            >
              <div
                style={{
                  transform: "scale(1.4)",
                  transformOrigin: "center",
                  filter: "drop-shadow(0 0 10px rgba(171, 235, 247, 0.35))",
                }}
              >
                <Icons type="logoIcon1" />
              </div>
            </div>

            {/* Tagline */}
            <h5
              className="xe-overline text-center text-text-lv3 mb-8"
              style={{
                opacity: visibleOpacity,
                transform: elementTransform,
                transition: "all 600ms cubic-bezier(0.16, 1, 0.3, 1) 300ms",
              }}
            >
              BEYOND THE TRADITIONAL PATH LIES
              <br />A UNIVERSE OF OPPORTUNITY
            </h5>

            {/* Form */}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div
                style={{
                  opacity: visibleOpacity,
                  transform: elementTransform,
                  transition: "all 600ms cubic-bezier(0.16, 1, 0.3, 1) 400ms",
                }}
              >
                <label htmlFor="login-email" className="xe-field-label">
                  email
                </label>
                <div className="relative">
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    onChange={() => setErrors({ error: "" })}
                    aria-invalid={errors.error ? true : undefined}
                    className="w-full min-h-[50px] px-4 pr-12 py-3 rounded-xl text-white text-md transition-all duration-300 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charlotte/60"
                    style={getInputStyle(errors.error)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    required
                  />
                  {errors.error && (
                    <Icons
                      type="warning"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                    />
                  )}
                </div>
              </div>

              {/* Password Input */}
              <div
                style={{
                  opacity: visibleOpacity,
                  transform: elementTransform,
                  transition: "all 600ms cubic-bezier(0.16, 1, 0.3, 1) 500ms",
                }}
              >
                <label htmlFor="login-password" className="xe-field-label">
                  password
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={isPasswordVisible ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    onChange={() => setErrors({ error: "" })}
                    aria-invalid={errors.error ? true : undefined}
                    className="w-full min-h-[50px] px-4 pr-12 py-3 rounded-xl text-white text-md transition-all duration-300 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charlotte/60"
                    style={getInputStyle(errors.error)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    required
                  />
                  {errors.error ? (
                    <Icons
                      type="warning"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                    />
                  ) : (
                    /* F02: the existing toggle, now a real button so a keyboard
                       user can reach it (it was an svg with a click handler).
                       Same handler, same state; type="button" so it can never
                       submit the form. */
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      aria-label={
                        isPasswordVisible ? "hide password" : "show password"
                      }
                      aria-pressed={isPasswordVisible}
                      className="xe-focus absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full opacity-70 transition-opacity hover:opacity-100"
                    >
                      <Icons
                        type={isPasswordVisible ? "invisible" : "visible"}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </div>
              </div>

              {/* Error message & forgot password */}
              <div
                className="flex justify-between items-center"
                style={{
                  opacity: visibleOpacity,
                  transition: "opacity 600ms ease 600ms",
                }}
              >
                <span className="xe-field-error">{errors.error}</span>
                {/* Forgot password - temporarily hidden (teacher-initiated reset available in admin)
                <Link
                  to="/forgot-password"
                  className="text-persimmon hover:text-red-200 text-sm font-medium uppercase tracking-wide transition-colors"
                >
                  FORGOT PASSWORD
                </Link>
                */}
              </div>

              {/* Launch Button */}
              <div
                style={{
                  opacity: visibleOpacity,
                  transform: elementTransform,
                  transition: "all 600ms cubic-bezier(0.16, 1, 0.3, 1) 700ms",
                }}
              >
                <ArrowFillButton
                  type="submit"
                  disabled={isLoading}
                  aria-busy={isLoading ? "true" : undefined}
                  tone="dark"
                  size="standard"
                  arrow={!isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      LAUNCHING...
                    </span>
                  ) : (
                    "LOGIN"
                  )}
                </ArrowFillButton>
              </div>

              {/* Clever Login */}
              {isCleverEnabled && (
                <>
                  <div
                    className="flex items-center my-4"
                    style={{
                      opacity: visibleOpacity,
                      transition: "opacity 600ms ease 750ms",
                    }}
                  >
                    <div className="flex-1 border-t border-white/20"></div>
                    <span className="xe-overline px-4 text-text-lv4">OR</span>
                    <div className="flex-1 border-t border-white/20"></div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCleverLogin}
                    disabled={isLoading}
                    className="xe-action xe-action--secondary w-full"
                    style={{
                      opacity: visibleOpacity,
                      transform: elementTransform,
                      transition:
                        "all 600ms cubic-bezier(0.16, 1, 0.3, 1) 800ms",
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8z"
                        fill="currentColor"
                      />
                      <path
                        d="M10 5c-2.761 0-5 2.239-5 5s2.239 5 5 5 5-2.239 5-5-2.239-5-5-5zm0 8c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z"
                        fill="currentColor"
                      />
                    </svg>
                    Sign in with Clever
                  </button>
                </>
              )}
            </form>

            {/* Sign up link - temporarily hidden
            <p
              className="text-center text-white/70 mt-6 text-base"
              style={{
                opacity: visibleOpacity,
                transition: "opacity 600ms ease 800ms",
              }}
            >
              don't have an account?{" "}
              <Link
                to="/register"
                className="text-persimmon hover:text-red-200 font-bold uppercase transition-colors"
              >
                SIGN UP!
              </Link>
            </p>
            */}
          </div>

          {/* Tagline below card on larger screens */}
          <p
            className="hidden lg:block text-center text-text-lv4 mt-8 text-lg font-light"
            style={{
              opacity: visibleOpacity,
              transform: elementTransform,
              transition: "all 800ms cubic-bezier(0.16, 1, 0.3, 1) 1000ms",
            }}
          >
            explore. discover. conquer.
            <br />
            <span className="text-charlotte/70">
              your mission of mastery begins with us
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};
